import { Injectable, OnDestroy } from '@angular/core';
import { firstValueFrom, Subscription } from 'rxjs';
import { PracticeFacilityService } from 'src/app/api/proxy/practice/services/practice-facility.service';
import { UserInfoService } from '../auth-service/user-info-service';
import { AlertController } from '@ionic/angular';
import { PracticePractitionerService } from 'src/app/api/proxy/practice/services/practice-practitioner.service';
import { FacilityModel } from 'src/app/api/proxy/practice/models/facility-model';
import { PromptService } from '../promtp-service/prompt.service';
import { Events } from '../events/events';
import { SecureStorageService } from '../secure-storage/secure-storage-service';
import { FacilityStatusType } from 'src/app/api/proxy/practice/practice-models';

@Injectable({
    providedIn: 'root'
})
export class PractitionerService implements OnDestroy {

    private practitionerFacilities?: FacilityModel[] | null;
    private selectedFacility?: FacilityModel | null;
    private facilitiesRequest: Promise<FacilityModel[]> | null = null;
    subscriptions: Subscription[] = [];

    constructor(
        protected alertCtrl: AlertController,
        protected _facilityService: PracticeFacilityService,
        protected practicePractitionerService: PracticePractitionerService,
        protected promptService: PromptService,
    ) {
        this.subscriptions.push(Events.facilitiesUpdated.subscribe(async (data: FacilityModel[]) => {
            this.practitionerFacilities = data;
            await this.getSelectedFacility();
        }));
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((a) => { a.unsubscribe() });
    }

    async setPractitionerInfo() {
        const userData = await UserInfoService.getUserInfo();
        if (userData?.practitionerId) {
            const practitionerData = await firstValueFrom(
                this.practicePractitionerService.practitionerIdGet({
                    id: userData.practitionerId,
                }),
            );
            await UserInfoService.updateFromPractitionerModel(practitionerData);
        }
    }

    async getFacilities(): Promise<FacilityModel[]> {
        if (!this.practitionerFacilities) {
           await this.setFacilities();
        }

        return this.practitionerFacilities || [];
    }

    async getSelectedFacility(): Promise<FacilityModel | null> {

        let savedPractice = await SecureStorageService.getWithOnlyPrefix<FacilityModel>(SecureStorageService.Prefix.Practice);
        if (savedPractice != null) {
            this.selectedFacility = savedPractice;
            return savedPractice;
        }

        return null;
    }

    async selectFacility(facility: FacilityModel) {
        await SecureStorageService.saveOnlyWithPrefix(facility, SecureStorageService.Prefix.Practice);
        this.selectedFacility = facility;
    }

    get hasSelectedFacility(): boolean {
        return !!this.selectedFacility;
    }

    get isSelectedFacilityInactive(): boolean {
        if (!this.selectedFacility) {
            return false;
        }
        return this.selectedFacility.status !== FacilityStatusType.Active;
    }

    get facilityActive(): boolean {
        if (!this.selectedFacility) {
            return false;
        }
        return this.selectedFacility.status === FacilityStatusType.Active;
    }

    public async setFacilities(forceUpdate: boolean = false): Promise<FacilityModel[]> {
        if (this.practitionerFacilities && !forceUpdate) {
            return this.practitionerFacilities;
        }

        if (this.facilitiesRequest) {
            return this.facilitiesRequest;
        }

        this.facilitiesRequest = (async () => {
            try {
                const result = (await firstValueFrom(
                    this.practicePractitionerService.facilitiesGet()
                )) as FacilityModel[];
                if (result) {
                    this.practitionerFacilities = result;
                    Events.facilitiesUpdated.emit(this.practitionerFacilities);
                    return result;
                }
            } catch (error) {
                this.promptService.showNetworkConnectionError(error, 'practitioner-fetch');
            }
            return this.practitionerFacilities || [];
        })();

        try {
            return await this.facilitiesRequest;
        } finally {
            this.facilitiesRequest = null;
        }
    }
}